import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import CaptionsModal from './components/CaptionsModal';
import DeleteModal from './components/DeleteModal';
import MetadataModal from './components/MetadataModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function formatDuration(startTime, endTime) {
  if (!startTime || !endTime) return 'N/A';
  const start = Number(startTime);
  const end = Number(endTime);
  if (isNaN(start) || isNaN(end) || end <= start) return 'N/A';

  const totalMinutes = Math.round((end - start) / 60000);
  if (totalMinutes < 60) {
    return `${totalMinutes} min${totalMinutes === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins}m`;
}

function formatDate(timestamp) {
  if (!timestamp) return 'N/A';
  const date = new Date(Number(timestamp));
  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function QuickAccess() {
  const urlParams = new URLSearchParams(window.location.search);
  const initialApiUrl = urlParams.get('apiUrl') || '';
  const initialSecret = urlParams.get('secret') || '';
  const initialPage = parseInt(urlParams.get('page'), 10) || 1;
  const initialLimit = parseInt(urlParams.get('limit'), 10) || 20;

  const [apiUrl, setApiUrl] = useState(initialApiUrl);
  const [secret, setSecret] = useState(initialSecret);
  const [showSecret, setShowSecret] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [totalCount, setTotalCount] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  // Filters & Sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [formatFilter, setFormatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Modals state
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [captionsRecording, setCaptionsRecording] = useState(null);
  const [deleteRecording, setDeleteRecording] = useState(null);
  const [metadataRecording, setMetadataRecording] = useState(null);

  const menuRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync state to URL search parameters
  const updateUrlParams = (newApiUrl, newSecret, newPage, newLimit) => {
    const params = new URLSearchParams();
    if (newApiUrl) params.set('apiUrl', newApiUrl);
    if (newSecret) params.set('secret', newSecret);
    if (newPage && newPage > 1) params.set('page', String(newPage));
    if (newLimit && newLimit !== 20) params.set('limit', String(newLimit));

    const newQuery = params.toString();
    const newRelativePathQuery = window.location.pathname + (newQuery ? '?' + newQuery : '');
    window.history.replaceState(null, '', newRelativePathQuery);
  };

  useEffect(() => {
    if (initialApiUrl && initialSecret) {
      fetchRecordings(initialPage, initialLimit, initialApiUrl, initialSecret);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const fetchRecordings = async (
    requestedPage = page,
    requestedLimit = limit,
    targetUrl = apiUrl,
    targetSecret = secret
  ) => {
    if (!targetUrl || !targetSecret) {
      setError('Please provide both BigBlueButton API URL and Shared Secret.');
      return;
    }

    setLoading(true);
    setError('');
    setActiveMenuId(null);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/recordings/public?page=${requestedPage}&limit=${requestedLimit}`,
        {
          apiUrl: targetUrl,
          secret: targetSecret
        }
      );

      const recs = res.data.recordings || [];
      setRecordings(recs);
      setTotalCount(res.data.total ?? recs.length);
      setPage(requestedPage);
      updateUrlParams(targetUrl, targetSecret, requestedPage, requestedLimit);
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.message ||
        'Failed to connect to BigBlueButton server. Please check the URL and Secret.';
      setError(errorMsg);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRecordings(1, limit, apiUrl, secret);
  };

  const handleClear = () => {
    setApiUrl('');
    setSecret('');
    setRecordings([]);
    setError('');
    setTotalCount(null);
    updateUrlParams('', '', 1, 20);
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?apiUrl=${encodeURIComponent(
      apiUrl
    )}&secret=${encodeURIComponent(secret)}&page=${page}&limit=${limit}`;

    navigator.clipboard.writeText(shareUrl).then(() => {
      showToast('Shareable link copied to clipboard!');
    });
  };

  const handleCopyText = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text).then(() => {
      showToast(`${label} copied!`);
    });
  };

  // Toggle publish status
  const handleTogglePublish = async (recording) => {
    const currentPublish = String(recording.published) === 'true';
    const nextPublish = !currentPublish;
    setActionLoadingId(recording.recordID);

    try {
      await axios.post(`${API_BASE_URL}/api/recordings/publish`, {
        apiUrl,
        secret,
        recordID: recording.recordID,
        publish: nextPublish
      });

      setRecordings((prev) =>
        prev.map((r) => (r.recordID === recording.recordID ? { ...r, published: String(nextPublish) } : r))
      );
      showToast(`Recording ${nextPublish ? 'published' : 'unpublished'} successfully!`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update publish status.');
    } finally {
      setActionLoadingId(null);
      setActiveMenuId(null);
    }
  };

  const handleRecordingDeleted = (recordId) => {
    setRecordings((prev) => prev.filter((r) => r.recordID !== recordId));
    setTotalCount((prev) => (prev ? prev - 1 : 0));
    showToast('Recording deleted successfully.');
  };

  const handleRecordingUpdated = (recordId, updatedMeta) => {
    setRecordings((prev) =>
      prev.map((r) => (r.recordID === recordId ? { ...r, name: updatedMeta.name } : r))
    );
    showToast('Recording metadata updated.');
  };

  // Filter and Sort recordings client-side within the current page
  const filteredRecordings = useMemo(() => {
    let list = [...recordings];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (r) =>
          (r.name && r.name.toLowerCase().includes(q)) ||
          (r.recordID && r.recordID.toLowerCase().includes(q)) ||
          (r.meetingID && r.meetingID.toLowerCase().includes(q))
      );
    }

    if (formatFilter !== 'all') {
      list = list.filter((r) => {
        const formats = Array.isArray(r.playback?.format)
          ? r.playback.format
          : r.playback?.format
          ? [r.playback.format]
          : [];
        return formats.some((f) => f?.type?.toLowerCase() === formatFilter);
      });
    }

    if (statusFilter === 'published') {
      list = list.filter((r) => String(r.published) === 'true');
    } else if (statusFilter === 'unpublished') {
      list = list.filter((r) => String(r.published) !== 'true');
    }

    list.sort((a, b) => {
      if (sortBy === 'newest') return Number(b.startTime || 0) - Number(a.startTime || 0);
      if (sortBy === 'oldest') return Number(a.startTime || 0) - Number(b.startTime || 0);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });

    return list;
  }, [recordings, searchQuery, formatFilter, statusFilter, sortBy]);

  const totalPages = totalCount ? Math.ceil(totalCount / limit) : 1;

  return (
    <div>
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{ position: 'fixed', top: '1.5rem', right: '1.5rem', zIndex: 9999 }}>
          <div className="toast-notice">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            {toastMessage}
          </div>
        </div>
      )}

      {/* Credentials Card */}
      <div className="card" style={{ padding: '1.75rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--slate-900)' }}>
              BigBlueButton Cluster Credentials
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--slate-500)', marginTop: '2px' }}>
              Connect directly to your BigBlueButton server using its API Endpoint and Shared Secret.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {recordings.length > 0 && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleCopyLink}
                  title="Copy link with these credentials and page parameters"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy Shareable Link
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => fetchRecordings(page, limit, apiUrl, secret)}
                  title="Refresh recordings"
                  disabled={loading}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                  Refresh
                </button>
              </>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="bbb-url">
                BigBlueButton API URL
              </label>
              <input
                id="bbb-url"
                name="url"
                type="url"
                autoComplete="url"
                className="form-control"
                placeholder="https://testnode1.bbbhost.cloud/bigbluebutton/"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                required
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="bbb-secret">
                Shared Secret
              </label>
              <div className="input-wrapper">
                <input
                  id="bbb-secret"
                  name="secret"
                  type={showSecret ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="form-control"
                  style={{ paddingRight: '2.5rem' }}
                  placeholder="Enter 40-character BBB shared secret"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="input-icon-btn"
                  onClick={() => setShowSecret(!showSecret)}
                  aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                  title={showSecret ? 'Hide secret' : 'Show secret'}
                >
                  {showSecret ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: 'var(--slate-500)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <span>Zero client data exposure • Direct BigBlueButton API proxy</span>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {(apiUrl || secret) && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear} disabled={loading}>
                  Clear
                </button>
              )}
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="12"></circle>
                    </svg>
                    Fetching Recordings...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                    Fetch Recordings
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ background: 'var(--danger-light)', border: '1px solid #fecaca', borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginBottom: '2rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <span style={{ color: 'var(--danger)', marginTop: '2px', display: 'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </span>
          <div>
            <strong style={{ color: 'var(--danger-text)', fontSize: '0.925rem' }}>Connection Failed:</strong>
            <p style={{ color: 'var(--danger-text)', fontSize: '0.875rem', marginTop: '2px' }}>{error}</p>
          </div>
        </div>
      )}

      {/* Recordings Dashboard Section */}
      {recordings.length > 0 && (
        <div>
          {/* Controls Bar: Search, Filter, Sort, Limit */}
          <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', alignItems: 'center' }}>
              {/* Search */}
              <div className="input-wrapper">
                <input
                  type="text"
                  className="form-control"
                  style={{ paddingLeft: '2.25rem' }}
                  placeholder="Search meeting or record ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <span style={{ position: 'absolute', left: '0.75rem', color: 'var(--slate-400)', display: 'flex' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </span>
              </div>

              {/* Format Filter */}
              <select
                className="form-control"
                value={formatFilter}
                onChange={(e) => setFormatFilter(e.target.value)}
              >
                <option value="all">All Playback Formats</option>
                <option value="video">Video (MP4) only</option>
                <option value="presentation">Presentation only</option>
                <option value="notes">Notes only</option>
              </select>

              {/* Status Filter */}
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="published">Published only</option>
                <option value="unpublished">Unpublished only</option>
              </select>

              {/* Sort By */}
              <select
                className="form-control"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Sort: Newest First</option>
                <option value="oldest">Sort: Oldest First</option>
                <option value="name">Sort: Meeting Name (A-Z)</option>
              </select>
            </div>

            {/* Quick summary line */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--slate-200)', fontSize: '0.825rem', color: 'var(--slate-500)', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                Showing <strong>{filteredRecordings.length}</strong> of <strong>{recordings.length}</strong> on this page (Total on server: <strong>{totalCount ?? recordings.length}</strong>)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Records per page:</span>
                <select
                  style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--slate-300)' }}
                  value={limit}
                  onChange={(e) => {
                    const newLimit = Number(e.target.value);
                    setLimit(newLimit);
                    fetchRecordings(1, newLimit, apiUrl, secret);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Recordings Table */}
          <div className="table-responsive" ref={menuRef}>
            <table className="recording-table">
              <thead>
                <tr>
                  <th style={{ width: '32%' }}>Meeting Details</th>
                  <th style={{ width: '18%' }}>Date & Time</th>
                  <th style={{ width: '10%' }}>Duration</th>
                  <th style={{ width: '10%' }}>Status</th>
                  <th style={{ width: '22%' }}>Playback & Downloads</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecordings.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--slate-500)' }}>
                      No recordings match your search or filter criteria.
                    </td>
                  </tr>
                ) : (
                  filteredRecordings.map((rec) => {
                    const formats = Array.isArray(rec.playback?.format)
                      ? rec.playback.format
                      : rec.playback?.format
                      ? [rec.playback.format]
                      : [];

                    const videoFormat = formats.find(
                      (f) =>
                        f.type?.toLowerCase() === 'video' ||
                        f.url?.toLowerCase().endsWith('.mp4') ||
                        f.type?.toLowerCase() === 'mp4'
                    );

                    const isPublished = String(rec.published) === 'true';
                    const isMenuOpen = activeMenuId === rec.recordID;
                    const isRowBusy = actionLoadingId === rec.recordID;

                    return (
                      <tr key={rec.recordID} style={{ opacity: isRowBusy ? 0.5 : 1 }}>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--slate-900)', fontSize: '0.925rem', marginBottom: '2px' }}>
                            {rec.name || 'Untitled Meeting'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'var(--slate-500)' }}>
                            <span style={{ fontFamily: 'monospace', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={rec.recordID}>
                              {rec.recordID}
                            </span>
                            <button
                              type="button"
                              className="btn-icon"
                              style={{ padding: '2px', border: 'none' }}
                              onClick={() => handleCopyText(rec.recordID, 'Record ID')}
                              title="Copy Record ID"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </button>
                          </div>
                        </td>

                        <td>
                          <div style={{ fontSize: '0.85rem', color: 'var(--slate-800)' }}>
                            {formatDate(rec.startTime)}
                          </div>
                        </td>

                        <td>
                          <span style={{ fontSize: '0.85rem', color: 'var(--slate-600)' }}>
                            {formatDuration(rec.startTime, rec.endTime)}
                          </span>
                        </td>

                        <td>
                          <button
                            type="button"
                            onClick={() => handleTogglePublish(rec)}
                            disabled={isRowBusy}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            title={`Click to ${isPublished ? 'unpublish' : 'publish'}`}
                          >
                            <span className={`badge ${isPublished ? 'badge-success' : 'badge-warning'}`}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isPublished ? '#059669' : '#d97706' }}></span>
                              {isPublished ? 'Published' : 'Unpublished'}
                            </span>
                          </button>
                        </td>

                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                            {formats.length === 0 ? (
                              <span style={{ fontSize: '0.8rem', color: 'var(--slate-400)' }}>No playback</span>
                            ) : (
                              formats.map((fmt, idx) => {
                                const isVideo =
                                  fmt.type?.toLowerCase() === 'video' ||
                                  fmt.url?.toLowerCase().endsWith('.mp4');

                                return (
                                  <a
                                    key={idx}
                                    href={fmt.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`format-pill ${isVideo ? 'format-video' : ''}`}
                                    title={`Open ${fmt.type || 'format'} playback`}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                    {fmt.type || 'Play'}
                                  </a>
                                );
                              })
                            )}

                            {/* Prominent Direct MP4 Download Button */}
                            {videoFormat && (
                              <a
                                href={videoFormat.url}
                                download={`${(rec.name || 'recording').replace(/[^a-zA-Z0-9_-]/g, '_')}_${rec.recordID.slice(0, 8)}.mp4`}
                                target="_blank"
                                rel="noreferrer"
                                className="download-mp4-btn"
                                title="Download MP4 Video File (If blocked by CORS, right-click and save video)"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Download MP4
                              </a>
                            )}
                          </div>
                        </td>

                        {/* Triple-Dot Actions Menu */}
                        <td style={{ textAlign: 'center' }}>
                          <div className="action-dropdown">
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={() => setActiveMenuId(isMenuOpen ? null : rec.recordID)}
                              title="More options"
                              aria-label="More options"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="1.5"></circle>
                                <circle cx="12" cy="5" r="1.5"></circle>
                                <circle cx="12" cy="19" r="1.5"></circle>
                              </svg>
                            </button>

                            {isMenuOpen && (
                              <div className="dropdown-menu">
                                {videoFormat && (
                                  <>
                                    <a
                                      href={videoFormat.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="dropdown-item"
                                      onClick={() => setActiveMenuId(null)}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                      </svg>
                                      Open Video in New Tab
                                    </a>
                                    <div className="dropdown-divider"></div>
                                  </>
                                )}

                                {formats.length > 0 && (
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={() => {
                                      handleCopyText(formats[0].url, 'Playback URL');
                                      setActiveMenuId(null);
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                    </svg>
                                    Copy Playback URL
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setCaptionsRecording(rec);
                                    setActiveMenuId(null);
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                                    <line x1="6" y1="15" x2="10" y2="15"></line>
                                    <line x1="14" y1="15" x2="18" y2="15"></line>
                                  </svg>
                                  Manage Captions (.vtt)
                                </button>

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setMetadataRecording(rec);
                                    setActiveMenuId(null);
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                  </svg>
                                  Edit Meeting Title
                                </button>

                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => handleTogglePublish(rec)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  {isPublished ? 'Unpublish Recording' : 'Publish Recording'}
                                </button>

                                <div className="dropdown-divider"></div>

                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  onClick={() => {
                                    setDeleteRecording(rec);
                                    setActiveMenuId(null);
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  </svg>
                                  Delete Recording
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--slate-500)' }}>
              Page <strong>{page}</strong> of <strong>{totalPages || 1}</strong>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fetchRecordings(page - 1, limit, apiUrl, secret)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => fetchRecordings(page + 1, limit, apiUrl, secret)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CaptionsModal
        isOpen={Boolean(captionsRecording)}
        onClose={() => setCaptionsRecording(null)}
        recording={captionsRecording}
        apiUrl={apiUrl}
        secret={secret}
        apiBaseUrl={API_BASE_URL}
      />

      <DeleteModal
        isOpen={Boolean(deleteRecording)}
        onClose={() => setDeleteRecording(null)}
        recording={deleteRecording}
        apiUrl={apiUrl}
        secret={secret}
        apiBaseUrl={API_BASE_URL}
        onDeleted={handleRecordingDeleted}
      />

      <MetadataModal
        isOpen={Boolean(metadataRecording)}
        onClose={() => setMetadataRecording(null)}
        recording={metadataRecording}
        apiUrl={apiUrl}
        secret={secret}
        apiBaseUrl={API_BASE_URL}
        onUpdated={handleRecordingUpdated}
      />
    </div>
  );
}

export default QuickAccess;
